import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import RoleDashboardPage from '@/pages/shared/RoleDashboardPage'
import PortalListPage from '@/pages/shared/PortalListPage'
import { api } from '@/lib/apiClient'

// ─── tiny helpers ────────────────────────────────────────────────────────────

function Msg({ ok, text }: { ok: boolean; text: string }) {
  if (!text) return null
  return (
    <div className={`mb-4 rounded-lg p-3 text-sm ${ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
      {text}
    </div>
  )
}

function Input({ label, name, value, onChange, type = 'text', required }: {
  label: string; name: string; value: string; onChange: (v: string) => void;
  type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}{required && ' *'}</label>
      <input
        type={type} name={name} value={value} onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  )
}

function Select({ label, name, value, onChange, options, required }: {
  label: string; name: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}{required && ' *'}</label>
      <select
        name={name} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function FormCard({ title, onClose, onSubmit, submitting, children }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void;
  submitting: boolean; children: React.ReactNode;
}) {
  return (
    <div className="mb-6 border border-blue-200 rounded-xl bg-blue-50 p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-blue-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg leading-none">✕</button>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        {children}
        <button
          type="submit" disabled={submitting}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  )
}

// ─── DEPT ADMIN DASHBOARD ─────────────────────────────────────────────────────

export function DeptAdminDashboard() {
  return (
    <RoleDashboardPage
      title="Department Admin"
      apiPath="/dept-admin/dashboard"
      links={[
        { label: 'Courses', to: '/dept-admin/courses' },
        { label: 'Classes', to: '/dept-admin/classes' },
        { label: 'Units', to: '/dept-admin/units' },
        { label: 'Trainers', to: '/dept-admin/trainers' },
        { label: 'Students', to: '/dept-admin/students' },
        { label: 'Exam Bookings', to: '/dept-admin/exam-bookings' },
        { label: 'Marks', to: '/dept-admin/marks' },
        { label: 'Attachments', to: '/dept-admin/attachments' },
        { label: 'Companies', to: '/dept-admin/companies' },
      ]}
    />
  )
}

// ─── COURSES ─────────────────────────────────────────────────────────────────

export function DeptAdminCourses() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Record<string, string> | null>(null)
  const [msg, setMsg] = useState({ ok: true, text: '' })
  const [form, setForm] = useState({ name: '', code: '', course_id: '' })
  const f = (k: string) => (v: string) => setForm((p) => ({ ...p, [k]: v }))

  const { data: res } = useQuery({ queryKey: ['dept-admin-courses'], queryFn: () => api.get('/dept-admin/courses').then((r: any) => r.data) })
  const courses: Record<string, string>[] = res?.data?.courses || []

  const save = useMutation({
    mutationFn: (body: Record<string, string>) => api.post('/dept-admin/courses', body).then((r: any) => r.data as any),
    onSuccess: (d: any) => {
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Error' }); return }
      setMsg({ ok: true, text: 'Saved.' })
      setShowForm(false); setEditing(null); setForm({ name: '', code: '', course_id: '' })
      qc.invalidateQueries({ queryKey: ['dept-admin-courses'] })
    },
    onError: () => setMsg({ ok: false, text: 'Request failed.' }),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.post('/dept-admin/courses', { delete: id }).then((r: any) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dept-admin-courses'] }),
  })

  function openEdit(row: Record<string, string>) {
    setEditing(row)
    setForm({ name: row.name || '', code: row.code || '', course_id: row.id || '' })
    setShowForm(true)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (editing) {
      save.mutate({ action: 'edit', course_id: editing.id, name: form.name, code: form.code })
    } else {
      save.mutate({ action: 'add', name: form.name, code: form.code })
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: '', code: '', course_id: '' }) }}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold">
          {showForm && !editing ? 'Cancel' : '+ Add Course'}
        </button>
      </div>
      <Msg {...msg} />
      {showForm && (
        <FormCard title={editing ? 'Edit Course' : 'New Course'} onClose={() => setShowForm(false)} onSubmit={submit} submitting={save.isPending}>
          <Input label="Course Name" name="name" value={form.name} onChange={f('name')} required />
          <Input label="Code" name="code" value={form.code} onChange={f('code')} required />
        </FormCard>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Code</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
          </tr></thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono">{c.code}</td>
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => openEdit(c)} className="text-blue-600 hover:underline text-xs">Edit</button>
                  <button onClick={() => { if (confirm('Delete course?')) del.mutate(c.id) }}
                    className="text-red-500 hover:underline text-xs">Delete</button>
                </td>
              </tr>
            ))}
            {courses.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No courses</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── CLASSES ─────────────────────────────────────────────────────────────────

export function DeptAdminClasses() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Record<string, string> | null>(null)
  const [msg, setMsg] = useState({ ok: true, text: '' })
  const [form, setForm] = useState({ name: '', course_id: '', intake_year: '', intake_month: '', level: '', cycle: '', class_id: '' })
  const f = (k: string) => (v: string) => setForm((p) => ({ ...p, [k]: v }))

  const { data: res } = useQuery({ queryKey: ['dept-admin-classes'], queryFn: () => api.get('/dept-admin/classes').then((r: any) => r.data) })
  const classes: Record<string, string>[] = res?.data?.classes || []
  const courses: { id: string; name: string; code: string }[] = res?.data?.courses || []
  const courseOpts = courses.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))

  const save = useMutation({
    mutationFn: (body: Record<string, string>) => api.post('/dept-admin/classes', body).then((r: any) => r.data as any),
    onSuccess: (d: any) => {
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Error' }); return }
      setMsg({ ok: true, text: 'Saved.' })
      setShowForm(false); setEditing(null)
      setForm({ name: '', course_id: '', intake_year: '', intake_month: '', level: '', cycle: '', class_id: '' })
      qc.invalidateQueries({ queryKey: ['dept-admin-classes'] })
    },
    onError: () => setMsg({ ok: false, text: 'Request failed.' }),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.post('/dept-admin/classes', { action: 'delete', class_id: id }).then((r: any) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dept-admin-classes'] }),
  })

  function openEdit(row: Record<string, string>) {
    setEditing(row)
    setForm({ name: row.name || '', course_id: row.course_id || '', intake_year: row.intake_year || '', intake_month: row.intake_month || '', level: row.level || '', cycle: row.cycle || '', class_id: row.id || '' })
    setShowForm(true)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (editing) {
      save.mutate({ action: 'edit', class_id: form.class_id, name: form.name, course_id: form.course_id, intake_year: form.intake_year, intake_month: form.intake_month, level: form.level, cycle: form.cycle })
    } else {
      save.mutate({ action: 'create', name: form.name, course_id: form.course_id, intake_year: form.intake_year, intake_month: form.intake_month, level: form.level, cycle: form.cycle })
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Classes</h1>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: '', course_id: '', intake_year: '', intake_month: '', level: '', cycle: '', class_id: '' }) }}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold">
          {showForm && !editing ? 'Cancel' : '+ Add Class'}
        </button>
      </div>
      <Msg {...msg} />
      {showForm && (
        <FormCard title={editing ? 'Edit Class' : 'New Class'} onClose={() => setShowForm(false)} onSubmit={submit} submitting={save.isPending}>
          <Input label="Class Name" name="name" value={form.name} onChange={f('name')} required />
          <Select label="Course" name="course_id" value={form.course_id} onChange={f('course_id')} options={courseOpts} required />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Intake Year" name="intake_year" value={form.intake_year} onChange={f('intake_year')} />
            <Input label="Intake Month" name="intake_month" value={form.intake_month} onChange={f('intake_month')} />
            <Input label="Level" name="level" value={form.level} onChange={f('level')} />
            <Input label="Cycle" name="cycle" value={form.cycle} onChange={f('cycle')} />
          </div>
        </FormCard>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Course</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Level / Cycle</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
          </tr></thead>
          <tbody>
            {classes.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold">{c.name}</td>
                <td className="px-4 py-3">{(c as any).courses?.name || c.course_id}</td>
                <td className="px-4 py-3">{c.level}{c.cycle ? ` · ${c.cycle}` : ''}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => openEdit(c)} className="text-blue-600 hover:underline text-xs">Edit</button>
                  <button onClick={() => { if (confirm('Delete class?')) del.mutate(c.id) }}
                    className="text-red-500 hover:underline text-xs">Delete</button>
                </td>
              </tr>
            ))}
            {classes.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No classes</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── UNITS ───────────────────────────────────────────────────────────────────

export function DeptAdminUnits() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Record<string, string> | null>(null)
  const [msg, setMsg] = useState({ ok: true, text: '' })
  const [form, setForm] = useState({ code: '', name: '', course_id: '', unit_id: '' })
  const f = (k: string) => (v: string) => setForm((p) => ({ ...p, [k]: v }))

  const { data: res } = useQuery({ queryKey: ['dept-admin-units'], queryFn: () => api.get('/dept-admin/units').then((r: any) => r.data) })
  const units: Record<string, string>[] = res?.data?.units || []
  const courses: { id: string; name: string; code: string }[] = res?.data?.courses || []
  const courseOpts = courses.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))

  const save = useMutation({
    mutationFn: (body: Record<string, string>) => api.post('/dept-admin/units', body).then((r: any) => r.data as any),
    onSuccess: (d: any) => {
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Error' }); return }
      setMsg({ ok: true, text: 'Saved.' })
      setShowForm(false); setEditing(null); setForm({ code: '', name: '', course_id: '', unit_id: '' })
      qc.invalidateQueries({ queryKey: ['dept-admin-units'] })
    },
    onError: () => setMsg({ ok: false, text: 'Request failed.' }),
  })

  function openEdit(row: Record<string, string>) {
    setEditing(row)
    setForm({ code: row.code || '', name: row.name || '', course_id: row.course_id || '', unit_id: row.id || '' })
    setShowForm(true)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (editing) {
      save.mutate({ action: 'edit', unit_id: form.unit_id, code: form.code, name: form.name, course_id: form.course_id })
    } else {
      save.mutate({ action: 'create', code: form.code, name: form.name, course_id: form.course_id })
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Units</h1>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ code: '', name: '', course_id: '', unit_id: '' }) }}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold">
          {showForm && !editing ? 'Cancel' : '+ Add Unit'}
        </button>
      </div>
      <Msg {...msg} />
      {showForm && (
        <FormCard title={editing ? 'Edit Unit' : 'New Unit'} onClose={() => setShowForm(false)} onSubmit={submit} submitting={save.isPending}>
          <Input label="Unit Code" name="code" value={form.code} onChange={f('code')} required />
          <Input label="Unit Name" name="name" value={form.name} onChange={f('name')} required />
          <Select label="Course" name="course_id" value={form.course_id} onChange={f('course_id')} options={courseOpts} required />
        </FormCard>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Code</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
          </tr></thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono">{u.code}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => openEdit(u)} className="text-blue-600 hover:underline text-xs">Edit</button>
                  <button onClick={() => { if (confirm('Delete unit?')) save.mutate({ action: 'delete', unit_id: u.id }) }}
                    className="text-red-500 hover:underline text-xs">Delete</button>
                </td>
              </tr>
            ))}
            {units.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No units</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── TRAINERS ────────────────────────────────────────────────────────────────

export function DeptAdminTrainers() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [msg, setMsg] = useState({ ok: true, text: '' })
  const [creds, setCreds] = useState<{ full_name: string; email: string; password: string } | null>(null)
  const [form, setForm] = useState({ full_name: '', email: '', staff_no: '' })
  const f = (k: string) => (v: string) => setForm((p) => ({ ...p, [k]: v }))

  const { data: res } = useQuery({ queryKey: ['dept-admin-trainers'], queryFn: () => api.get('/dept-admin/trainers').then((r: any) => r.data) })
  const trainers: Record<string, string>[] = res?.data?.trainers || []

  const create = useMutation({
    mutationFn: (body: Record<string, string>) => api.post('/dept-admin/trainers', body).then((r: any) => r.data as any),
    onSuccess: (d: any) => {
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Error' }); return }
      setMsg({ ok: true, text: 'Trainer created.' })
      setCreds(d.data?.new_creds || null)
      setShowForm(false); setForm({ full_name: '', email: '', staff_no: '' })
      qc.invalidateQueries({ queryKey: ['dept-admin-trainers'] })
    },
    onError: () => setMsg({ ok: false, text: 'Request failed.' }),
  })

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Trainers</h1>
        <button onClick={() => { setShowForm(!showForm); setCreds(null) }}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold">
          {showForm ? 'Cancel' : '+ Add Trainer'}
        </button>
      </div>
      <Msg {...msg} />
      {creds && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
          <p className="font-semibold text-green-800 mb-1">Trainer created — share these credentials:</p>
          <p><strong>Name:</strong> {creds.full_name}</p>
          <p><strong>Email:</strong> {creds.email}</p>
          <p><strong>Password:</strong> <code className="bg-white px-1 rounded">{creds.password}</code></p>
        </div>
      )}
      {showForm && (
        <FormCard title="New Trainer" onClose={() => setShowForm(false)} onSubmit={(e) => { e.preventDefault(); create.mutate({ full_name: form.full_name, email: form.email, staff_no: form.staff_no }) }} submitting={create.isPending}>
          <Input label="Full Name" name="full_name" value={form.full_name} onChange={f('full_name')} required />
          <Input label="Email" name="email" value={form.email} onChange={f('email')} type="email" required />
          <Input label="Staff No" name="staff_no" value={form.staff_no} onChange={f('staff_no')} />
        </FormCard>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Staff No</th>
          </tr></thead>
          <tbody>
            {trainers.map((t) => (
              <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">{t.full_name}</td>
                <td className="px-4 py-3">{t.email}</td>
                <td className="px-4 py-3">{t.staff_no || '—'}</td>
              </tr>
            ))}
            {trainers.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No trainers</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── STUDENTS ────────────────────────────────────────────────────────────────

export function DeptAdminStudents() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [msg, setMsg] = useState({ ok: true, text: '' })
  const [creds, setCreds] = useState<{ full_name: string; admission_no: string; email: string; password: string } | null>(null)
  const [form, setForm] = useState({ full_name: '', email: '', admission_no: '', class_id: '' })
  const f = (k: string) => (v: string) => setForm((p) => ({ ...p, [k]: v }))

  const { data: res } = useQuery({ queryKey: ['dept-admin-students'], queryFn: () => api.get('/dept-admin/students').then((r: any) => r.data) })
  const students: Record<string, string>[] = res?.data?.students || []
  const classes: { id: string; name: string }[] = res?.data?.classes || []
  const classOpts = classes.map((c) => ({ value: c.id, label: c.name }))

  const create = useMutation({
    mutationFn: (body: Record<string, string>) => api.post('/dept-admin/students', body).then((r: any) => r.data as any),
    onSuccess: (d: any) => {
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Error' }); return }
      setMsg({ ok: true, text: 'Student created.' })
      setCreds(d.data?.new_creds || null)
      setShowForm(false); setForm({ full_name: '', email: '', admission_no: '', class_id: '' })
      qc.invalidateQueries({ queryKey: ['dept-admin-students'] })
    },
    onError: () => setMsg({ ok: false, text: 'Request failed.' }),
  })

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Students</h1>
        <button onClick={() => { setShowForm(!showForm); setCreds(null) }}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold">
          {showForm ? 'Cancel' : '+ Add Student'}
        </button>
      </div>
      <Msg {...msg} />
      {creds && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
          <p className="font-semibold text-green-800 mb-1">Student enrolled — share these credentials:</p>
          <p><strong>Name:</strong> {creds.full_name}</p>
          <p><strong>Admission No:</strong> {creds.admission_no}</p>
          <p><strong>Email:</strong> {creds.email}</p>
          <p><strong>Password:</strong> <code className="bg-white px-1 rounded">{creds.password}</code></p>
        </div>
      )}
      {showForm && (
        <FormCard title="Enrol New Student" onClose={() => setShowForm(false)}
          onSubmit={(e) => { e.preventDefault(); create.mutate({ full_name: form.full_name, email: form.email, admission_no: form.admission_no, class_id: form.class_id }) }}
          submitting={create.isPending}>
          <Input label="Full Name" name="full_name" value={form.full_name} onChange={f('full_name')} required />
          <Input label="Admission No" name="admission_no" value={form.admission_no} onChange={f('admission_no')} required />
          <Input label="Email" name="email" value={form.email} onChange={f('email')} type="email" required />
          <Select label="Class" name="class_id" value={form.class_id} onChange={f('class_id')} options={classOpts} required />
        </FormCard>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Adm No</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
          </tr></thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono">{s.admission_no}</td>
                <td className="px-4 py-3">{s.full_name}</td>
                <td className="px-4 py-3">{s.email}</td>
              </tr>
            ))}
            {students.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No students</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── COMPANIES ───────────────────────────────────────────────────────────────

export function DeptAdminCompanies() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Record<string, string> | null>(null)
  const [msg, setMsg] = useState({ ok: true, text: '' })
  const [form, setForm] = useState({ name: '', county: '', physical_address: '', contact_person: '', contact_phone: '', email: '', company_id: '' })
  const f = (k: string) => (v: string) => setForm((p) => ({ ...p, [k]: v }))

  const { data: res } = useQuery({ queryKey: ['dept-admin-companies'], queryFn: () => api.get('/dept-admin/companies').then((r: any) => r.data) })
  const companies: Record<string, string>[] = res?.data?.companies || []

  const save = useMutation({
    mutationFn: (body: Record<string, string>) => api.post('/dept-admin/companies', body).then((r: any) => r.data as any),
    onSuccess: (d: any) => {
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Error' }); return }
      setMsg({ ok: true, text: 'Saved.' })
      setShowForm(false); setEditing(null)
      setForm({ name: '', county: '', physical_address: '', contact_person: '', contact_phone: '', email: '', company_id: '' })
      qc.invalidateQueries({ queryKey: ['dept-admin-companies'] })
    },
    onError: () => setMsg({ ok: false, text: 'Request failed.' }),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.post('/dept-admin/companies', { action: 'delete', company_id: id }).then((r: any) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dept-admin-companies'] }),
  })

  function openEdit(row: Record<string, string>) {
    setEditing(row)
    setForm({ name: row.name || '', county: row.county || '', physical_address: row.physical_address || '', contact_person: row.contact_person || '', contact_phone: row.contact_phone || '', email: row.email || '', company_id: row.id || '' })
    setShowForm(true)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const body = { ...form }
    if (editing) { save.mutate({ action: 'edit', ...body }) }
    else { save.mutate({ action: 'create', ...body }) }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: '', county: '', physical_address: '', contact_person: '', contact_phone: '', email: '', company_id: '' }) }}
          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold">
          {showForm && !editing ? 'Cancel' : '+ Add Company'}
        </button>
      </div>
      <Msg {...msg} />
      {showForm && (
        <FormCard title={editing ? 'Edit Company' : 'New Company'} onClose={() => setShowForm(false)} onSubmit={submit} submitting={save.isPending}>
          <Input label="Company Name" name="name" value={form.name} onChange={f('name')} required />
          <div className="grid grid-cols-2 gap-3">
            <Input label="County" name="county" value={form.county} onChange={f('county')} />
            <Input label="Physical Address" name="physical_address" value={form.physical_address} onChange={f('physical_address')} />
            <Input label="Contact Person" name="contact_person" value={form.contact_person} onChange={f('contact_person')} />
            <Input label="Contact Phone" name="contact_phone" value={form.contact_phone} onChange={f('contact_phone')} />
            <Input label="Email" name="email" value={form.email} onChange={f('email')} type="email" />
          </div>
        </FormCard>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">County</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Contact</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
          </tr></thead>
          <tbody>
            {companies.map((co) => (
              <tr key={co.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold">{co.name}</td>
                <td className="px-4 py-3">{co.county || '—'}</td>
                <td className="px-4 py-3">{co.contact_person || '—'} {co.contact_phone ? `· ${co.contact_phone}` : ''}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => openEdit(co)} className="text-blue-600 hover:underline text-xs">Edit</button>
                  <button onClick={() => { if (confirm('Delete company?')) del.mutate(co.id) }}
                    className="text-red-500 hover:underline text-xs">Delete</button>
                </td>
              </tr>
            ))}
            {companies.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No companies</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── REMAINING DEPT ADMIN (read-only / simple) ────────────────────────────────

export function DeptAdminAssignUnits() {
  return <RoleDashboardPage title="Assign Units" apiPath="/dept-admin/assign-units" />
}

export function DeptAdminAttendance() {
  return <RoleDashboardPage title="Attendance" apiPath="/dept-admin/attendance" />
}

export function DeptAdminAssessments() {
  return (
    <PortalListPage
      title="Assessments"
      apiPath="/dept-admin/assessments"
      listKey="assessments"
      columns={[
        { key: 'assessment_type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'user_profiles.full_name', label: 'Student' },
      ]}
    />
  )
}

export function DeptAdminExamBookings() {
  return (
    <PortalListPage
      title="Exam Bookings"
      apiPath="/dept-admin/exam-bookings"
      listKey="bookings"
      columns={[
        { key: 'serial_number', label: 'Serial' },
        { key: 'status', label: 'Status' },
      ]}
      actions={[
        {
          label: 'Approve',
          path: (row) => `/dept-admin/exam-bookings/${row.id}/approve`,
          confirm: 'Approve this booking?',
        },
        {
          label: 'Reject',
          path: (row) => `/dept-admin/exam-bookings/${row.id}/reject`,
          confirm: 'Reject this booking?',
        },
      ]}
    />
  )
}

export function DeptAdminMarks() {
  return <RoleDashboardPage title="Marks" apiPath="/dept-admin/marks" />
}

export function DeptAdminTraineePoe() {
  return <RoleDashboardPage title="Trainee POE" apiPath="/dept-admin/trainee-poe" />
}

export function DeptAdminAttachments() {
  return (
    <PortalListPage
      title="Attachments"
      apiPath="/dept-admin/attachments"
      listKey="attachments"
      columns={[
        { key: 'status', label: 'Status' },
        { key: 'start_date', label: 'Start' },
      ]}
    />
  )
}

export function DeptAdminLogbooks() {
  return (
    <PortalListPage
      title="Logbooks"
      apiPath="/dept-admin/logbooks"
      listKey="logbooks"
      columns={[
        { key: 'log_date', label: 'Date' },
        { key: 'activities', label: 'Activities' },
      ]}
    />
  )
}

export function DeptAdminApplications() {
  return (
    <PortalListPage
      title="Applications"
      apiPath="/dept-admin/applications"
      listKey="applications"
      columns={[{ key: 'status', label: 'Status' }]}
    />
  )
}

// ─── SUPER ADMIN ─────────────────────────────────────────────────────────────

export function SuperAdminDashboard() {
  return (
    <RoleDashboardPage
      title="Super Admin"
      apiPath="/super-admin/dashboard"
      links={[
        { label: 'Departments', to: '/super-admin/departments' },
        { label: 'Users', to: '/super-admin/users' },
        { label: 'Logs', to: '/super-admin/logs' },
      ]}
    />
  )
}

export function SuperAdminDepartments() {
  return (
    <PortalListPage
      title="Departments"
      apiPath="/super-admin/departments"
      listKey="departments"
      columns={[
        { key: 'code', label: 'Code' },
        { key: 'name', label: 'Name' },
      ]}
    />
  )
}

export function SuperAdminUsers() {
  return (
    <PortalListPage
      title="Users"
      apiPath="/super-admin/users"
      listKey="users"
      columns={[
        { key: 'full_name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'role', label: 'Role' },
      ]}
    />
  )
}

export function SuperAdminLogs() {
  return (
    <PortalListPage
      title="Audit Logs"
      apiPath="/super-admin/logs"
      listKey="logs"
      columns={[
        { key: 'action', label: 'Action' },
        { key: 'created_at', label: 'When' },
      ]}
    />
  )
}
